/**
 * PAYMENT DETAILS' CARD - the first thing on Payments, and the separate card at the end of the
 * Author profile page (design 25 section 6; the owner's D1, 2026-10-11: "a clear section, obvious in
 * the UI, not lost in the dashboard or among the profile's inputs").
 *
 * WHAT IT SAYS. "Complete" or "Not complete"; the PayPal email MASKED (the page itself shows it in
 * full, D5) and the country by its name; while not complete, what is missing and the day it is
 * needed by; and "Open Payment details", the way to the page. It is a summary and never a form:
 * changing where money goes stays behind the page's fresh sign-in.
 *
 * WHAT IT READS. `getPayoutMethod` itself, so a page places it with no plumbing. "Today", for the
 * needed-by line, is an instant the server sent (C11): a page that already holds one hands it in
 * (`today`, Payments passes its view's `computedAt`); left out, the card reads the payments view
 * itself; `null` says "not known" and reads nothing, and the line then says what is true until the
 * day comes.
 */
import { Card, Group, Stack, Text } from '@mantine/core';
import { Wallet } from '@moonx/ui';
import { useCallback, useId } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import type { PaymentsView } from '../../features/cabinet/api';
import type { PaymentDetails } from '../../features/cabinet/api/cabinet-api';
import { LoadingState, PartialFailureNote, SectionLabel } from '../../features/cabinet/components';
import { formatInstant } from '../../features/cabinet/format';
import { useAsync } from '../../features/cabinet/state';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { Trans, useTranslation } from '../../i18n';
import { countryName, isComplete, missingLine, requiredFromLine } from './payment-details';
import { CompletenessPill, PAYOUT_METHOD_PATH } from './PayoutMethodRoute';

export interface PaymentDetailsSummaryCardProps {
  /** Where "Open Payment details" goes: the page (./PayoutMethodRoute.tsx) unless told otherwise. */
  readonly href?: string;
  /**
   * The server's "now", an instant (the payments view's `computedAt`). Left out, the card reads the
   * payments view for it; `null` means not known, and nothing is read.
   */
  readonly today?: string | null;
  /** The level a screen reader hears: 2 on its own, 3 where it sits under another section. */
  readonly headingLevel?: 2 | 3;
}

export function PaymentDetailsSummaryCard({
  href = PAYOUT_METHOD_PATH,
  today,
  headingLevel = 2,
}: PaymentDetailsSummaryCardProps) {
  const { t } = useTranslation('payments');
  const api = useCabinetApi();
  const headingId = useId();
  const ownDay = today === undefined;

  const readMethod = useCallback((signal: AbortSignal) => api.getPayoutMethod(signal), [api]);
  const readDay = useCallback(
    (signal: AbortSignal): Promise<PaymentsView | undefined> =>
      ownDay ? api.getPaymentsView(signal) : Promise.resolve(undefined),
    [api, ownDay],
  );
  const method = useAsync(readMethod, 'payment-details:card');
  // A refusal (an account on hold) or a failure only means the day is not known.
  const view = useAsync(readDay, `payment-details:card-day:${String(ownDay)}`);
  const now = today === undefined ? view.data?.computedAt : today;

  return (
    <section aria-labelledby={headingId}>
      <SectionLabel id={headingId} order={headingLevel}>
        {t('card.heading')}
      </SectionLabel>
      {method.status === 'loading' && method.data === undefined ? (
        <LoadingState label={t('card.reading')} />
      ) : method.status === 'error' ? (
        // Partial: whatever page this sits on is still right when this card is not.
        <PartialFailureNote what={t('card.failed')} error={method.error} />
      ) : method.data === undefined ? null : (
        <SummaryCard method={method.data} href={href} today={now} />
      )}
    </section>
  );
}

function SummaryCard({
  method,
  href,
  today,
}: {
  readonly method: PaymentDetails;
  readonly href: string;
  readonly today: string | null | undefined;
}) {
  const { t } = useTranslation('payments');
  const complete = isComplete(method);
  const missing = complete ? undefined : missingLine(method.missing);
  const needed = complete ? undefined : requiredFromLine(method.requiredFrom, today);
  const country = method.recipientCountry;

  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group gap="sm" wrap="nowrap" align="flex-start" justify="space-between">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <Wallet aria-hidden="true" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            {method.isSet ? (
              <Stack gap={4}>
                <Text size="sm">
                  <Trans
                    t={t}
                    i18nKey="card.paypal"
                    values={{ address: method.paypal?.maskedEmail ?? t('card.addressOnFile') }}
                    components={{ strong: <strong /> }}
                  />
                </Text>
                {country === undefined || country === null ? null : (
                  <Text size="sm">{t('card.country', { country: countryName(country) })}</Text>
                )}
                <Text size="xs" c="dimmed">
                  {method.updatedAt === undefined || method.updatedAt === null
                    ? t('card.masked')
                    : t('card.maskedChanged', { when: formatInstant(method.updatedAt) })}
                </Text>
              </Stack>
            ) : (
              <Stack gap={4}>
                <Text size="sm">{t('card.notAdded')}</Text>
                <Text size="xs" c="dimmed">
                  {t('card.notAddedNote')}
                </Text>
              </Stack>
            )}
          </Group>
          <CompletenessPill method={method} />
        </Group>
        {missing === undefined && needed === undefined ? null : (
          <Stack gap={2}>
            {missing === undefined ? null : <Text size="sm">{missing}</Text>}
            {needed === undefined ? null : (
              <Text size="xs" c="dimmed">
                {needed}
              </Text>
            )}
          </Stack>
        )}
        <Group>
          <InAppAnchor href={href} size="sm">
            {t('card.open')}
          </InAppAnchor>
        </Group>
      </Stack>
    </Card>
  );
}
